// A scan SESSION (ActionDef.capture.loop): scan one code after another, each
// checked by the action's business logic and CONFIRMED by the user before it
// touches a line. The host (ChildFormTab) owns what a check means — which
// line, which values — and hands back what to show and how to apply it; this
// file is only the loop:
//
//   scan ──► checking ──► confirm ──(Confirm: apply / Cancel)──► scan …
//                    └──► notice (a refused code, a few seconds) ──► scan …
//
// The camera is the shared ScanCapture, opened ONE-SHOT per code (a fresh
// mount each time), so a label still in view can never be read again while
// the user is looking at the confirm card. Closing the scanner ends the
// session.

import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ScanCapture } from '../scan/ScanCapture'
import { tw } from '../testHooks'

/** One column of the line, as the confirm card shows it. */
export interface ScanSessionDetail {
  label: string
  value: string
  /** The value the line holds now, when the scan changes it. */
  was?: string
}

export type ScanSessionCheck =
  | { kind: 'refused'; message: string }
  // ``apply`` answers true when that was the LAST line to scan: the session
  // then closes by itself.
  | { kind: 'match'; title: string; details: ScanSessionDetail[]; apply: () => boolean }

/** How long a refused code's message stays before scanning resumes. */
const NOTICE_MS = 3000

const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 9800,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
}
const sheetStyle: React.CSSProperties = {
  width: 'min(520px, 96vw)', maxHeight: '90vh', background: 'var(--color-surface, #fff)',
  color: 'var(--color-text, #1f2937)', borderRadius: 14, padding: 16,
  display: 'flex', flexDirection: 'column', gap: 12, boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
}
const btn: React.CSSProperties = {
  padding: '8px 16px', fontSize: '0.9rem', borderRadius: 8, cursor: 'pointer',
  border: '1px solid var(--color-border, #ccc)', background: 'transparent', color: 'var(--color-text, #111)',
}

type Phase =
  | { at: 'scan' }
  | { at: 'checking'; code: string }
  | { at: 'notice'; code: string; message: string }
  | { at: 'confirm'; code: string; check: Extract<ScanSessionCheck, { kind: 'match' }> }

export function ScanSession({ title, prompt, remaining, check, onClose }: {
  title: string
  prompt?: string
  /** Lines still to scan, when the action declares it (shown in the title). */
  remaining?: number | null
  check: (code: string) => Promise<ScanSessionCheck>
  onClose: () => void
}) {
  const [phase, setPhase] = useState<Phase>({ at: 'scan' })
  const [confirmed, setConfirmed] = useState(0)
  // A fresh scanner per code (see the header).
  const [scanKey, setScanKey] = useState(0)
  const live = useRef(true)
  useEffect(() => () => { live.current = false }, [])

  const next = useCallback(() => {
    setScanKey(k => k + 1)
    setPhase({ at: 'scan' })
  }, [])

  const onScan = useCallback((code: string) => {
    setPhase({ at: 'checking', code })
    check(code)
      .then(res => {
        if (!live.current) return
        setPhase(res.kind === 'match'
          ? { at: 'confirm', code, check: res }
          : { at: 'notice', code, message: res.message })
      })
      .catch(() => { if (live.current) setPhase({ at: 'notice', code, message: 'This code could not be checked.' }) })
  }, [check])

  // A refused code: show it for a moment, then the next scan.
  useEffect(() => {
    if (phase.at !== 'notice') return
    const t = window.setTimeout(next, NOTICE_MS)
    return () => window.clearTimeout(t)
  }, [phase, next])

  const confirm = useCallback(() => {
    if (phase.at !== 'confirm') return
    const finished = phase.check.apply()
    setConfirmed(n => n + 1)
    if (finished) { onClose(); return }
    next()
  }, [phase, next, onClose])

  // Esc cancels the card. Deliberately no Enter shortcut and no focused
  // Confirm: an external (keyboard-wedge) scanner ends every code with Enter,
  // and a scan must never confirm a card nobody looked at.
  useEffect(() => {
    if (phase.at !== 'confirm') return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); next() }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [phase, confirm, next])

  const count = (confirmed === 1 ? '1 confirmed' : `${confirmed} confirmed`)
    + (typeof remaining === 'number' ? ` · ${remaining} left` : '')
  if (phase.at === 'scan') {
    return (
      <ScanCapture
        key={scanKey}
        title={`${prompt || title} · ${count}`}
        onScan={onScan}
        onClose={onClose} />
    )
  }
  if (typeof document === 'undefined') return null // SSR safety

  return createPortal(
    <div style={overlayStyle}>
      <div style={sheetStyle} data-tw-dialog="scan_session" onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 700, fontSize: 16 }}>{title} · {count}</span>
          <button onClick={onClose} {...tw('action', 'close')} style={{ ...btn, padding: '4px 12px' }}
            title="Stop scanning">Done</button>
        </div>

        {phase.at === 'checking' && (
          <div style={{ fontSize: '0.95rem', color: 'var(--color-text-secondary, #555)' }}>
            Checking <code>{phase.code}</code>…
          </div>
        )}

        {phase.at === 'notice' && (
          <div role="alert" data-tw-scan-flash="error" style={{
            borderRadius: 10, padding: '14px 16px', color: '#fff', background: '#dc2626',
            display: 'flex', flexDirection: 'column', gap: 6,
          }}>
            <div style={{ fontWeight: 700 }}>Not accepted — <code>{phase.code}</code></div>
            <div style={{ fontSize: '0.95rem' }}>{phase.message}</div>
            <div style={{ fontSize: '0.8rem', opacity: 0.85 }}>Scanning the next code in a moment…</div>
          </div>
        )}

        {phase.at === 'confirm' && (
          <>
            <div style={{ fontSize: '0.95rem' }}>
              <div style={{ fontWeight: 600 }}>{phase.check.title}</div>
              <div style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary, #666)' }}>
                Scanned <code>{phase.code}</code>
              </div>
            </div>
            <div style={{ overflow: 'auto', border: '1px solid var(--color-border, #e5e5e5)', borderRadius: 8 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <tbody>
                  {phase.check.details.map(d => (
                    <tr key={d.label} style={{ borderBottom: '1px solid var(--color-border, #eee)' }}>
                      <td style={{ padding: '6px 10px', color: 'var(--color-text-secondary, #666)', whiteSpace: 'nowrap', verticalAlign: 'top' }}>{d.label}</td>
                      <td style={{ padding: '6px 10px', fontWeight: d.was !== undefined ? 600 : 400 }}>
                        {d.value || '—'}
                        {d.was !== undefined && (
                          <span style={{ marginLeft: 8, fontWeight: 400, fontSize: '0.8rem', color: 'var(--color-text-secondary, #888)' }}>
                            (was {d.was || 'blank'})
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={next} {...tw('action', 'cancel')} style={btn}>Cancel</button>
              <button onClick={confirm} {...tw('action', 'confirm')}
                style={{ ...btn, border: 'none', background: 'var(--color-accent, #4c8dff)', color: '#fff', fontWeight: 600 }}>
                Confirm
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  )
}
